import React, { useState, useEffect } from 'react'
import { Box, Stack, Typography, IconButton, Modal, Button, useTheme, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import TemporaryIcon from 'components/global/TemporaryIcon'
import { ActionIcon } from 'components/Modal/ActionModal'
import { TimeRange } from 'models'

import { useGetStatsTimeRange } from '../store'
import GraphActiveUsers from './SectionTypes/GraphActiveUsers'
import GraphConsumption from './SectionTypes/GraphConsumption'
import SSIDActiveUsers from './SectionSsids/GraphActiveUsers'
import SiteActiveUsers from './SectionSites/GraphActiveUsers'
import SSIDConsumption from './SectionSsids/GraphConsumption'
import SiteConsumption from './SectionSites/GraphConsumption'
import EvolutionActiveUsers from './SectionEvolution/GraphActiveUsers'
import EvolutionConsumption from './SectionEvolution/GraphConsumption'

interface GraphsSectionProps {
  viewType: 'type' | 'ssid' | 'site' | 'evolution'
}

const GraphContainer = ({ title, children, onExpand, showExpand = true }) => (
  <Box 
    sx={{ 
      border: '1px solid #EFEFEF', 
      borderRadius: '16px', 
      p: 2, 
      height: 'calc(50% - 8px)',
      position: 'relative'
    }}
  >
    <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: 2, position: 'relative' }}>
      <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
        {title}
      </Typography>
      {showExpand && (
        <IconButton
          size="small"
          onClick={onExpand}
          sx={{
            backgroundColor: '#F7F7F7',
            width: 24,
            height: 24,
            position: 'absolute',
            right: 0,
            '&:hover': {
              backgroundColor: '#EFEFEF'
            }
          }}
        >
          <TemporaryIcon 
            iconlabel="arrow-up-right-and-arrow-down-left-from-center" 
            iconsize="12px"
          />
        </IconButton>
      )}
    </Stack>
    <Box sx={{ height: 'calc(100% - 40px)' }}>
      {children}
    </Box>
  </Box>
)

const GraphsSection: React.FC<GraphsSectionProps> = ({ viewType }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'))
  const [expandedGraph, setExpandedGraph] = useState<string | null>(null)
  const [modalTimeRange, setModalTimeRange] = useState<TimeRange>(TimeRange.LastThirtyDays)
  const [globalTimeRange] = useGetStatsTimeRange()

  const showExpand = viewType !== 'evolution'

  useEffect(() => {
    if (expandedGraph && globalTimeRange) {
      setModalTimeRange(globalTimeRange)
    }
  }, [expandedGraph, globalTimeRange])

  const handleOpenModal = (graphType: string) => {
    if (viewType === 'evolution') return
    setExpandedGraph(graphType)
    setModalTimeRange(globalTimeRange)
  }

  const handleCloseModal = () => {
    setExpandedGraph(null)
  }

  const handleModalTimeRangeChange = (newTimeRange: TimeRange) => {
    setModalTimeRange(newTimeRange)
  }

  const timeRangeChoices = [
    {
      label: t('stats.range_select.last_30_days'),
      value: TimeRange.LastThirtyDays,
      id: 5,
    },
    {
      label: t('stats.range_select.last_month'),
      value: TimeRange.LastMonth,
      id: 10,
    },
    {
      label: t('stats.range_select.last_3_months'),
      value: TimeRange.LastThreeMonths,
      id: 11,
    },
    {
      label: t('stats.range_select.last_12_months'),
      value: TimeRange.LastTwelveMonths,
      id: 7,
      disabled: viewType === 'evolution'
    },
    {
      label: t('stats.range_select.last_year'),
      value: TimeRange.LastYear,
      id: 8,
    },
    {
      label: t('stats.range_select.last_5_years'),
      value: TimeRange.LastFiveYears,
      id: 9,
    },
  ]

  const renderExpandedGraph = () => {
    if (!expandedGraph || viewType === 'evolution') return null

    const modalStyle = {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '70%',
      maxWidth: 750,
      height: 560,
      bgcolor: 'background.paper',
      boxShadow: 24,
      p: 4,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1300
    }

    const buttonStyle = {
      borderRadius: '4px',
      padding: '6px 12px',
      minHeight: '32px',
      height: '32px',
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: 'white',
      fontWeight: 500,
      fontSize: '11px',
      color: theme.palette.grey[700],
      textTransform: 'none' as const,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:hover': {
        backgroundColor: '#F5F5F5',
      }
    }

    const activeButtonStyle = {
      ...buttonStyle,
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: '#EDF6FF',
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: '#E4F1FF',
      }
    }

    const disabledButtonStyle = {
      ...buttonStyle,
      backgroundColor: '#F5F5F5',
      color: theme.palette.grey[400],
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: '#F5F5F5',
      }
    }

    return (
      <Modal
        open={!!expandedGraph}
        onClose={handleCloseModal}
        aria-labelledby="graph-modal-title"
        sx={{ zIndex: 1300 }}
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <ActionIcon>
                <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
              </ActionIcon>
              <Typography
                id="graph-modal-title"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {expandedGraph === 'active-users' && t('stats.traffic.graphs.active_users_title')}
                {expandedGraph === 'consumption' && t('stats.traffic.graphs.data_consumption_title')}
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: 0.5
              }}>
                {timeRangeChoices.map((choice) => {
                  const isActive = modalTimeRange === choice.value
                  const isDisabled = choice.disabled
                  
                  return (
                    <Button
                      key={choice.id}
                      onClick={() => !isDisabled && handleModalTimeRangeChange(choice.value)}
                      disabled={isDisabled}
                      sx={isDisabled ? disabledButtonStyle : (isActive ? activeButtonStyle : buttonStyle)}
                    >
                      {choice.label}
                    </Button>
                  )
                })}
              </Box>
              <Button
                onClick={handleCloseModal}
                sx={{
                  minWidth: 28,
                  minHeight: 28,
                  padding: 0,
                  borderRadius: '50%',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: '#F0F0F0',
                  }
                }}
              >
                <TemporaryIcon iconlabel="xmark" iconsize="14px" />
              </Button>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', zIndex: 1000 }}>
            {expandedGraph === 'active-users' && (
              <>
                {viewType === 'type' && <GraphActiveUsers modalTimeRange={modalTimeRange} hasLegend={true} />}
                {viewType === 'ssid' && <SSIDActiveUsers modalTimeRange={modalTimeRange} hasLegend={true} />}
                {viewType === 'site' && <SiteActiveUsers modalTimeRange={modalTimeRange} hasLegend={true} />}
              </>
            )}
            {expandedGraph === 'consumption' && (
              <>
                {viewType === 'type' && <GraphConsumption modalTimeRange={modalTimeRange} hasLegend={true} />}
                {viewType === 'ssid' && <SSIDConsumption modalTimeRange={modalTimeRange} hasLegend={true} />}
                {viewType === 'site' && <SiteConsumption modalTimeRange={modalTimeRange} hasLegend={true} />}
              </>
            )}
          </Box>
        </Box>
      </Modal>
    )
  }

  return (
    <>
      <Stack sx={{ flex: 1, minWidth: 0, height: '100%' }} spacing={2}>
        <GraphContainer 
          title={t('stats.traffic.graphs.active_users_title')}
          onExpand={() => handleOpenModal('active-users')}
          showExpand={showExpand}
        >
          {viewType === 'type' && <GraphActiveUsers hasLegend={false}/>}
          {viewType === 'ssid' && <SSIDActiveUsers hasLegend={false} />}
          {viewType === 'site' && <SiteActiveUsers hasLegend={false} />}
          {viewType === 'evolution' && <EvolutionActiveUsers />}
        </GraphContainer>
        
        <GraphContainer 
          title={t('stats.traffic.graphs.data_consumption_title')}
          onExpand={() => handleOpenModal('consumption')}
          showExpand={showExpand}
        >
          {viewType === 'type' && <GraphConsumption hasLegend={false} />}
          {viewType === 'ssid' && <SSIDConsumption hasLegend={false} />}
          {viewType === 'site' && <SiteConsumption hasLegend={false} />}
          {viewType === 'evolution' && <EvolutionConsumption />}
        </GraphContainer>
      </Stack>
      
      {renderExpandedGraph()}
    </>
  )
}

export default GraphsSection